import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth, isTTC } from '../hooks/useAuth';
import { useCart } from '../hooks/useCart';
import { StatusBadge } from '../components/StatusBadge';
import { useBranding } from '../hooks/useBranding';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { RefreshCw, FileText } from 'lucide-react';
import ImageLightbox from '../components/ImageLightbox';

const NEXT_STATUSES = {
  submitted:     ['approved', 'cancelled'],
  approved:      ['in_production', 'cancelled'],
  in_production: ['shipped'],
  shipped:       ['delivered'],
  delivered:     ['closed'],
};

export default function OrderDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { defaultProductImage } = useBranding();
  const imgOf = (it) => it?.image_url || defaultProductImage || '/default-ticket.svg';
  const { addItem, items: cartItems } = useCart();
  const nav = useNavigate();
  const [orderDeleteEnabled, setOrderDeleteEnabled] = useState(false);

  useEffect(() => {
    if (isTTC(user)) api.get('/api/settings/general')
      .then(r => setOrderDeleteEnabled(!!r.data.order_delete_enabled)).catch(() => {});
  }, [user]);

  // A charge is either picked from the catalog (rate resolved server-side,
  // customer rate beating the generic one) or typed in by hand as before.
  const addCharge = async () => {
    const picked = catalog.find(c => String(c.id) === String(charge.type_id));
    const body = {};

    if (picked) {
      body.charge_type_id = picked.id;
      if (picked.unit !== 'flat') {
        if (charge.quantity === '' || isNaN(Number(charge.quantity)) || Number(charge.quantity) <= 0) {
          return toast.error('Enter how many');
        }
        body.quantity = Number(charge.quantity);
      }
      // Only send an amount when staff deliberately overrode the rate.
      if (charge.amount !== '' && !isNaN(Number(charge.amount))) body.amount = Number(charge.amount);
      else if (picked.effective_amount == null) return toast.error('No rate for this customer — enter an amount');
    } else {
      if (!charge.label.trim()) return toast.error('Give the charge a description');
      if (charge.amount === '' || isNaN(Number(charge.amount))) return toast.error('Enter an amount');
      body.label = charge.label.trim();
      body.amount = Number(charge.amount);
    }

    try {
      await api.post(`/api/orders/${id}/charges`, body);
      setCharge({ type_id: '', label: '', amount: '', quantity: '' });
      toast.success('Charge added');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not add the charge'); }
  };

  // Correct a charge in place. Changing the quantity re-prices from the rate the
  // charge was originally priced at, so a correction cannot drift off the card.
  const saveChargeEdit = async () => {
    const body = { label: editCharge.label };
    if (editCharge.quantity !== '' && editCharge.quantity != null) body.quantity = Number(editCharge.quantity);
    if (editCharge.amount !== '' && editCharge.amount != null) body.amount = Number(editCharge.amount);
    try {
      await api.put(`/api/orders/${id}/charges/${editCharge.id}`, body);
      setEditCharge(null);
      toast.success('Charge updated');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not update the charge'); }
  };

  const removeCharge = async (chargeId) => {
    try {
      await api.delete(`/api/orders/${id}/charges/${chargeId}`);
      toast.success('Charge removed');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not remove the charge'); }
  };

  const deleteOrder = async () => {
    if (!window.confirm(`Do you really want to delete order ${order?.order_number}?\n\nThis permanently removes the order and its files and cannot be undone.`)) return;
    try { await api.delete(`/api/orders/${id}`); toast.success('Order deleted'); nav('/orders'); }
    catch (e) { toast.error(e.response?.data?.error || 'Delete failed'); }
  };
  const [data, setData] = useState(null);
  const [zoomSrc, setZoomSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusNote, setStatusNote] = useState('');
  const [tracking, setTracking] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [charge, setCharge] = useState({ type_id: '', label: '', amount: '', quantity: '' });
  const [catalog, setCatalog] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [editCharge, setEditCharge] = useState(null);

  const load = () => {
    api.get(`/api/orders/${id}`).then(r => {
      setData(r.data);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, [id]);

  // The catalog is fetched for this order's customer so the picker shows the
  // rate that will actually be applied, not the generic one.
  const orderCustomerId = data?.order?.customer_id;
  useEffect(() => {
    if (!isTTC(user) || !orderCustomerId) return;
    api.get(`/api/charge-types?customer_id=${orderCustomerId}`)
      .then(r => setCatalog(r.data.charge_types || []))
      .catch(() => setCatalog([]));
  }, [user, orderCustomerId]);

  // What this order's own answers suggest. Read-only — nothing is applied until
  // someone presses Add. Re-read whenever the charges change, so an accepted
  // suggestion drops off the list.
  const chargeCount = data?.charges?.length ?? 0;
  useEffect(() => {
    if (!isTTC(user) || !data?.order?.id) return;
    api.get(`/api/orders/${id}/suggested-charges`)
      .then(r => setSuggestions(r.data.suggestions || []))
      .catch(() => setSuggestions([]));
  }, [user, id, data?.order?.id, chargeCount]);

  const applySuggestion = async (s) => {
    try {
      await api.post(`/api/orders/${id}/charges`, {
        charge_type_id: s.charge_type_id,
        ...(s.unit !== 'flat' ? { quantity: s.quantity } : {}),
      });
      toast.success(`${s.label} added`);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not add the charge'); }
  };

  const updateStatus = async (newStatus) => {
    setSaving(true);
    try {
      await api.patch(`/api/orders/${id}/status`, {
        status: newStatus,
        notes: statusNote,
        tracking_number: tracking || undefined,
      });
      toast.success(`Status updated to ${newStatus.replace(/_/g, ' ')}`);
      setStatusNote('');
      setTracking('');
      load();
    } catch (e) {
      toast.error('Failed to update status');
    } finally { setSaving(false); }
  };

  const uploadProof = async () => {
    if (!proofFile) return;
    const fd = new FormData();
    fd.append('proof', proofFile);
    setSaving(true);
    try {
      await api.post(`/api/orders/${id}/proof`, fd);
      toast.success('Proof uploaded');
      setProofFile(null);
      load();
    } catch (e) {
      toast.error('Upload failed');
    } finally { setSaving(false); }
  };

  const proofDecision = async (approved) => {
    const notes = approved ? '' : (prompt('Reason for rejection:') || '');
    await api.patch(`/api/orders/${id}/proof-decision`, { approved, notes });
    toast.success(approved ? 'Proof approved!' : 'Proof rejected');
    load();
  };

  const handleReorder = async () => {
    try {
      const r = await api.get(`/api/orders/${id}/reorder-data`);
      const reorderItems = r.data.items;
      const inactive = reorderItems.filter(i => !i.active);
      if (inactive.length) {
        toast.error(`${inactive.length} item(s) are no longer available and were skipped.`);
      }
      let added = 0;
      for (const item of reorderItems) {
        if (!item.active) continue;
        addItem(
          { id: item.product_id, name: item.product_name, sku: item.sku },
          item.quantity,
          item.attributes,
          item.price_per_m,
        );
        added++;
      }
      if (added > 0) {
        toast.success(`${added} item${added > 1 ? 's' : ''} added to cart`);
        nav('/cart');
      }
    } catch (e) {
      toast.error('Failed to load reorder data');
    }
  };

  if (loading) return <div className="loading">Loading…</div>;
  if (!data) return <div className="page"><div className="alert alert-error">Order not found.</div></div>;

  const { order, items, history, attachments, charges = [], perms } = data;
  // Summed from the rows rather than read off orders.charges_total, so the
  // figure on screen always matches the lines listed beneath it.
  const chargesTotal = charges.reduce((s, c) => s + Number(c.amount || 0), 0);
  const grandTotal = Number(order.total_amount || 0) + chargesTotal;
  // v1.44.0 — an order carrying a quote-required line has a PARTIAL total.
  const needsQuote = !!order.needs_quote || (order.items || []).some(i => i.quote_required);
  const canManage = isTTC(user);
  const nextStatuses = NEXT_STATUSES[order.status] || [];

  return (
    <>
      <div className="topbar">
        <h2>
          <Link to="/orders" style={{ color: 'var(--gray-500)', fontWeight: 400 }}>Orders</Link>
          {' / '}{order.order_number}
        </h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <StatusBadge status={order.status} />
          {perms?.place_orders && (
            <button className="btn btn-secondary btn-sm" onClick={handleReorder}>
              <RefreshCw size={14} /> Reorder
            </button>
          )}
          {canManage && (
            <button className="btn btn-secondary btn-sm" onClick={() => nav(`/orders/${id}/work-order`)}>Work order</button>
          )}
          {canManage && orderDeleteEnabled && (
            <button className="btn btn-danger btn-sm" onClick={deleteOrder}>Delete order</button>
          )}
        </div>
      </div>
      <div className="page">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>
          <div>
            {/* Order Info */}
            <div className="card mb-16">
              <div className="card-header"><h3>Order Details</h3></div>
              <div className="form-row">
                <div>
                  <div className="text-muted text-sm">Customer</div>
                  <div>{order.customer_name}</div>
                </div>
                <div>
                  <div className="text-muted text-sm">{order.po_field_label || 'PO Number'}</div>
                  <div>{order.po_number || '—'}</div>
                </div>
                <div>
                  <div className="text-muted text-sm">Placed</div>
                  <div>{format(new Date(order.created_at), 'MM/dd/yyyy h:mm a')}</div>
                </div>
                <div>
                  <div className="text-muted text-sm">Placed By</div>
                  <div>{order.first_name} {order.last_name}</div>
                  {order.contact_email && <div className="text-sm">{order.contact_email}</div>}
                  {order.contact_phone && <div className="text-sm">{order.contact_phone}</div>}
                </div>
              </div>
              {(order.ship_to_name || order.ship_to_address) && (
                <div className="mt-8">
                  <div className="text-muted text-sm">Ship To</div>
                  <div>{order.ship_to_name}</div>
                  {order.ship_to_address && (
                    <div className="text-sm text-muted">
                      {order.ship_to_address}, {order.ship_to_city}, {order.ship_to_state} {order.ship_to_zip}
                    </div>
                  )}
                </div>
              )}
              {order.notes && (
                <div className="mt-8">
                  <div className="text-muted text-sm">Notes</div>
                  <div>{order.notes}</div>
                </div>
              )}
              {order.tracking_number && (
                <div className="mt-8 alert alert-info">
                  Tracking: <strong>{order.tracking_number}</strong>
                  {order.shipped_date && ` · Shipped ${format(new Date(order.shipped_date), 'MM/dd/yyyy')}`}
                </div>
              )}
            </div>

            {/* Line Items */}
            <div className="card mb-16">
              <div className="card-header"><h3>Items</h3></div>
              {items.map((item, idx) => (
                <div key={item.id} style={{
                  padding: '14px 0',
                  borderBottom: idx < items.length - 1 ? '1px solid var(--gray-100)' : 'none',
                }}>
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <img src={imgOf(item)} alt="" onClick={() => setZoomSrc(imgOf(item))} title="Click to enlarge"
                        style={{ width: 44, height: 44, borderRadius: 6, objectFit: 'cover', flexShrink: 0, cursor: 'zoom-in' }} />
                      <div>
                        <strong>{item.product_name}</strong>
                        <span className="text-muted text-sm" style={{ marginLeft: 8 }}>{item.sku}</span>
                        {Object.entries(item.attributes || {}).map(([k, v]) =>
                          v ? <div key={k} className="text-sm text-muted">{k}: {v}</div> : null
                        )}
                        {item.notes && <div className="text-sm text-muted">Note: {item.notes}</div>}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div>{item.quantity.toLocaleString()} units</div>
                      {perms?.view_pricing && (
                        item.quote_required ? (
                          <div className="text-sm" style={{ color: 'var(--brand-red)' }}>Toledo Ticket to provide custom pricing</div>
                        ) : (
                          <div className="text-sm text-muted">${item.price_per_m.toFixed(2)}/M · <strong>${item.line_total.toFixed(2)}</strong></div>
                        )
                      )}
                    </div>
                  </div>

                  {/* Sample files */}
                  {item.sample_files?.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div className="text-sm text-muted" style={{ marginBottom: 4 }}>Sample Files:</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {item.sample_files.map(f => (
                          <a
                            key={f.id}
                            href={`/api/orders/files/sample/${f.filename}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              background: 'var(--blue-bg)', color: '#1565c0',
                              border: '1px solid #90caf9', borderRadius: 4,
                              padding: '2px 8px', fontSize: 12, textDecoration: 'none',
                            }}
                          >
                            <FileText size={11} /> {f.original_name}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {perms?.view_pricing && (
                <div style={{ paddingTop: 12, borderTop: '1px solid var(--gray-200)', marginTop: 4 }}>
                  {/* v1.44.0 — say the total is partial. Without this the figure
                      below reads as the finished price of the whole order. */}
                  {needsQuote && (
                    <p className="form-hint" style={{ textAlign: 'right', color: 'var(--brand-red)' }}>
                      This order includes an item Toledo Ticket prices by hand. The total below
                      covers the priced lines only.
                    </p>
                  )}
                  {charges.length > 0 && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24 }}>
                        <span className="text-sm text-muted">Items</span>
                        <span className="text-sm">${Number(order.total_amount || 0).toFixed(2)}</span>
                      </div>
                      {charges.map(c => (
                        <div key={c.id} style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, alignItems: 'center' }}>
                          <span className="text-sm text-muted">
                            {c.label}
                            {c.quantity != null && c.unit_rate != null && c.unit !== 'flat'
                              ? ` (${Number(c.quantity).toLocaleString()} × $${Number(c.unit_rate).toFixed(2)})` : ''}
                            {c.notes ? ` — ${c.notes}` : ''}
                          </span>
                          <span className="text-sm">${Number(c.amount).toFixed(2)}</span>
                          {canManage && (
                            <>
                              <button className="link-btn" onClick={() => setEditCharge({
                                id: c.id, label: c.label, quantity: c.quantity ?? '', amount: '',
                                unit: c.unit, unit_rate: c.unit_rate,
                              })}>edit</button>
                              <button className="link-btn" style={{ color: 'var(--brand-red)' }}
                                onClick={() => removeCharge(c.id)}>remove</button>
                            </>
                          )}
                        </div>
                      ))}

                      {editCharge && (
                        <div style={{ padding: 10, marginTop: 6, background: 'var(--gray-50)', borderRadius: 6, textAlign: 'left' }}>
                          <div className="form-row" style={{ alignItems: 'flex-end' }}>
                            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                              <label className="form-label">Description</label>
                              <input className="form-input" value={editCharge.label}
                                onChange={e => setEditCharge(c => ({ ...c, label: e.target.value }))} />
                            </div>
                            {editCharge.unit && editCharge.unit !== 'flat' && editCharge.unit_rate != null && (
                              <div className="form-group" style={{ flex: '0 0 120px', marginBottom: 0 }}>
                                <label className="form-label">How many</label>
                                <input className="form-input" type="number" step="any" min="0" value={editCharge.quantity}
                                  onChange={e => setEditCharge(c => ({ ...c, quantity: e.target.value }))} />
                              </div>
                            )}
                            <div className="form-group" style={{ flex: '0 0 130px', marginBottom: 0 }}>
                              <label className="form-label">Amount ($)</label>
                              <input className="form-input" type="number" step="0.01" value={editCharge.amount}
                                placeholder={editCharge.unit_rate != null && editCharge.unit !== 'flat' && editCharge.quantity !== ''
                                  ? (Number(editCharge.unit_rate) * Number(editCharge.quantity)).toFixed(2) : ''}
                                onChange={e => setEditCharge(c => ({ ...c, amount: e.target.value }))} />
                            </div>
                            <div className="form-group" style={{ flex: '0 0 auto', marginBottom: 0, display: 'flex', gap: 6 }}>
                              <button className="btn btn-primary btn-sm" onClick={saveChargeEdit}>Save</button>
                              <button className="btn btn-ghost btn-sm" onClick={() => setEditCharge(null)}>Cancel</button>
                            </div>
                          </div>
                          {editCharge.unit_rate != null && editCharge.unit !== 'flat' && (
                            <p className="form-hint" style={{ marginBottom: 0 }}>
                              Priced at ${Number(editCharge.unit_rate).toFixed(2)} each. Change the quantity and the
                              amount follows; enter an amount to override it.
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, marginTop: 6 }}>
                    <span className="fw-600">Order Total: ${grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {canManage && (
                <div style={{ borderTop: '1px solid var(--gray-200)', marginTop: 12, paddingTop: 12 }}>
                  {suggestions.length > 0 && (
                    <div style={{ marginBottom: 14, padding: '10px 12px', border: '1px dashed var(--gray-300)', borderRadius: 6 }}>
                      <div className="form-label" style={{ marginBottom: 2 }}>Suggested from this order's answers</div>
                      <p className="form-hint" style={{ marginBottom: 8 }}>
                        Nothing is charged until you add it. Check the working before you do.
                      </p>
                      {suggestions.map(s => (
                        <div key={s.charge_type_id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '5px 0' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13 }}>
                              <strong>{s.label}</strong>
                              {s.amount != null && <> — ${Number(s.amount).toFixed(2)}</>}
                              {s.unit !== 'flat' && s.quantity != null && s.unit_rate != null && (
                                <span className="text-muted"> ({Number(s.quantity).toLocaleString()} × ${Number(s.unit_rate).toFixed(2)})</span>
                              )}
                            </div>
                            <div className="form-hint" style={{ margin: 0 }}>
                              {s.why}{s.from_item ? ` — ${s.from_item}` : ''}
                            </div>
                            {s.needs_attention && (
                              <div className="form-hint" style={{ margin: 0, color: 'var(--brand-red)' }}>
                                {s.needs_attention} — add it by hand below
                              </div>
                            )}
                          </div>
                          {!s.needs_attention && (
                            <button className="btn btn-secondary btn-sm" style={{ flex: '0 0 auto' }}
                              onClick={() => applySuggestion(s)}>Add</button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {(() => {
                    const picked = catalog.find(c => String(c.id) === String(charge.type_id));
                    const qty = picked && picked.unit !== 'flat' ? Number(charge.quantity) : 1;
                    const preview = picked && picked.effective_amount != null && Number.isFinite(qty) && qty > 0
                      ? picked.effective_amount * qty : null;
                    return (
                      <>
                        <div className="form-row" style={{ alignItems: 'flex-end' }}>
                          {catalog.length > 0 && (
                            <div className="form-group" style={{ marginBottom: 0, flex: '0 0 220px' }}>
                              <label className="form-label">Additional charge</label>
                              <select className="form-select" value={charge.type_id}
                                onChange={e => setCharge(c => ({ ...c, type_id: e.target.value, amount: '', quantity: '' }))}>
                                <option value="">Type it in…</option>
                                {catalog.map(ct => <option key={ct.id} value={ct.id}>{ct.label}</option>)}
                              </select>
                            </div>
                          )}

                          {!picked && (
                            <div className="form-group" style={{ marginBottom: 0 }}>
                              <label className="form-label">{catalog.length > 0 ? 'Description' : 'Additional charge'}</label>
                              <input className="form-input" value={charge.label} placeholder="e.g. New plate charge"
                                onChange={e => setCharge(c => ({ ...c, label: e.target.value }))} />
                            </div>
                          )}

                          {picked && picked.unit !== 'flat' && (
                            <div className="form-group" style={{ marginBottom: 0, flex: '0 0 130px' }}>
                              <label className="form-label">{picked.unit_label.replace(/^Per /, 'How many ')}</label>
                              <input className="form-input" type="number" step="any" min="0" value={charge.quantity}
                                onChange={e => setCharge(c => ({ ...c, quantity: e.target.value }))} />
                            </div>
                          )}

                          <div className="form-group" style={{ marginBottom: 0, flex: '0 0 140px' }}>
                            <label className="form-label">{picked ? 'Override ($)' : 'Amount ($)'}</label>
                            <input className="form-input" type="number" step="0.01" value={charge.amount}
                              placeholder={preview != null ? preview.toFixed(2) : ''}
                              onChange={e => setCharge(c => ({ ...c, amount: e.target.value }))} />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0, flex: '0 0 auto' }}>
                            <button className="btn btn-secondary btn-sm" onClick={addCharge}>Add charge</button>
                          </div>
                        </div>

                        {picked && (
                          <p className="form-hint" style={{ marginBottom: 2 }}>
                            {picked.effective_amount == null
                              ? <>No rate set for this customer or generally — enter the amount by hand, or add one in the Charge Catalog.</>
                              : <>
                                  {picked.rate_source === 'customer' ? `${order.customer_name || 'This customer'}'s rate` : 'Generic rate'}
                                  {': $'}{Number(picked.effective_amount).toFixed(2)} {picked.unit_label.toLowerCase()}
                                  {preview != null && picked.unit !== 'flat' ? ` — ${qty.toLocaleString()} × $${Number(picked.effective_amount).toFixed(2)} = $${preview.toFixed(2)}` : ''}
                                  {'. Leave Override blank to use it.'}
                                </>}
                          </p>
                        )}
                        <p className="form-hint">
                          Added to this order only, and shown to the customer. Use a negative amount for a credit.
                        </p>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Proof Section */}
            {order.proof_required && (
              <div className="card mb-16">
                <div className="card-header"><h3>Proof Approval</h3></div>
                {!order.proof_url && canManage && (
                  <div>
                    <div className="text-muted text-sm mb-16">Upload a proof file for customer approval.</div>
                    <input type="file" onChange={e => setProofFile(e.target.files[0])} accept=".pdf,.jpg,.jpeg,.png" />
                    <button className="btn btn-primary mt-8" onClick={uploadProof} disabled={!proofFile || saving}>
                      Upload Proof
                    </button>
                  </div>
                )}
                {order.proof_url && order.proof_approved === null && (
                  <div className="proof-section">
                    <p className="mb-16">Proof is ready for review.</p>
                    <a href={order.proof_url} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">View Proof ↗</a>
                    {!canManage && perms?.approve_proof && (
                      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                        <button className="btn btn-gold" onClick={() => proofDecision(true)}>✓ Approve Proof</button>
                        <button className="btn btn-danger btn-sm" onClick={() => proofDecision(false)}>✗ Request Changes</button>
                      </div>
                    )}
                  </div>
                )}
                {order.proof_approved === 1 && <div className="alert alert-success">Proof approved ✓</div>}
                {order.proof_approved === 0 && <div className="alert alert-error">Proof rejected: {order.proof_notes}</div>}
              </div>
            )}

            {/* Attachments */}
            {attachments.length > 0 && (
              <div className="card">
                <div className="card-header"><h3>Attachments</h3></div>
                {attachments.map(a => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--gray-100)' }}>
                    <div>
                      <div className="text-sm">{a.original_name}</div>
                      <div className="text-sm text-muted">{a.first_name} {a.last_name} · {format(new Date(a.created_at), 'MM/dd/yyyy')}</div>
                    </div>
                    <a href={`/api/orders/files/proof/${a.filename}`} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">Download</a>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Column */}
          <div>
            {canManage && nextStatuses.length > 0 && (
              <div className="card mb-16">
                <div className="card-header"><h3>Update Status</h3></div>
                <div className="form-group">
                  <label className="form-label">Notes (optional)</label>
                  <textarea className="form-textarea" value={statusNote} onChange={e => setStatusNote(e.target.value)} rows={2} />
                </div>
                {(order.status === 'approved' || order.status === 'in_production') && (
                  <div className="form-group">
                    <label className="form-label">Tracking #</label>
                    <input className="form-input" value={tracking} onChange={e => setTracking(e.target.value)} />
                  </div>
                )}
                {nextStatuses.map(s => (
                  <button key={s}
                    className={`btn btn-sm ${s === 'cancelled' ? 'btn-danger' : 'btn-primary'}`}
                    style={{ display: 'block', width: '100%', marginBottom: 8 }}
                    onClick={() => updateStatus(s)}
                    disabled={saving}
                  >
                    → {s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </button>
                ))}
              </div>
            )}

            <div className="card">
              <div className="card-header"><h3>History</h3></div>
              <div className="timeline">
                {[...history].reverse().map((h, i) => (
                  <div className="timeline-item" key={h.id}>
                    <div className="timeline-line">
                      <div className="timeline-dot" />
                      {i < history.length - 1 && <div className="timeline-connector" />}
                    </div>
                    <div className="timeline-content">
                      <div className="msg">
                        {h.from_status ? `${h.from_status.replace(/_/g, ' ')} → ` : ''}
                        <strong>{h.to_status.replace(/_/g, ' ')}</strong>
                      </div>
                      {h.notes && <div className="text-sm text-muted">{h.notes}</div>}
                      <div className="ts">{h.first_name} {h.last_name} · {format(new Date(h.created_at), 'MM/dd/yyyy h:mm a')}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      {zoomSrc && <ImageLightbox src={zoomSrc} onClose={() => setZoomSrc(null)} />}
    </>
  );
}
